// Replace your existing PreviewSTTemplate with this (keeps your original imports)
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { useHistory, useParams } from "react-router-dom";
import makeAnimated from "react-select/animated";
import { Button } from "../../elements/Button";
import commoncss from "../../css/common.module.css";
import buttoncss from "../../css/button.module.css";
import Breadcrumb from "../../components/Breadcrumb";
import PreviewSidebar from "../../components/templateLibrary/PreviewSidebar";
import PreviewTemplateTableGenerator from "./PreviewTemplateTableGenerator";
import RenderBSPLTables from "../../components/templateLibrary/RenderBSPLTables";
import QuillReactEditor from "../../components/QuillReactEditor";
import libcss from "../../css/library.module.css";
import { getTemplateDetailsApiHandler } from "../../../WorkSpace/Components/ApiServices/apiHandler";

const genId = () => Math.random().toString(36).slice(2);

const normalizeFromInitial = (initialDoc) => {
  const items = [];
  (initialDoc?.data || []).forEach((block) => {
    if (block.dataType === "text") {
      items.push({
        id: genId(),
        type: "text",
        dataType: "text",
        textFormat: "html",
        textRaw: block.textRaw,
        textPlain: block.textPlain,
        text: [
          {
            id: genId(),
            content: block?.textRaw ?? "",
          },
        ],
      });
    } else if (block.dataType === "table") {
      items.push({
        id: genId(),
        type: "table",
        tableTitle: block.tableTitle || "Untitled Table",
        tableRow: Array.isArray(block.tableRow)
          ? block.tableRow.map((r) => ({ ...r }))
          : [],
        tableColumn: Array.isArray(block.tableColumn)
          ? block.tableColumn.map((c) => ({ ...c }))
          : [],
      });
    }
  });
  return items;
};

const PreviewSTTemplate = () => {
  const animatedComponents = makeAnimated();
  let history = useHistory();
  const dispatch = useDispatch();
  const [libraryName] = useState("ST Library");

  const [templateData, setTemplateData] = useState(null);
  const [selCat, setSelCat] = useState(null);
  const { tempId } = useParams();
  const [items, setItems] = useState(() => normalizeFromInitial(null));

  useEffect(() => {
    getTemplateDetails();
  }, []);

  const getTemplateDetails = () => {
    getTemplateDetailsApiHandler({ groupingTemplateID: tempId })
      .then((response) => {
        if (response?.data?.success === true) {
          setTemplateData(response?.data?.groupingTemplate);
        }
      })
      .catch((error) => {
        // handle error if needed
      });
  };

  // now showData accepts optional subIdx for second-level subNotes
  const showData = (note, idx, subIdx = null) => {
    setSelCat(note);
    if (note === "BS" || note === "PL") {
      // BS / PL handled elsewhere (tables)
      setItems(normalizeFromInitial(null));
      return;
    }

    const arr = templateData?.[note] || [];
    const target = typeof idx === "number" && arr[idx] ? arr[idx] : null;

    if (!target) {
      // fallback: empty
      setItems(normalizeFromInitial(null));
      return;
    }

    // if subIdx provided and the target has subNotes, show subNote's data
    if (
      subIdx !== null &&
      Array.isArray(target.subNotes) &&
      target.subNotes[subIdx]
    ) {
      setItems(normalizeFromInitial(target.subNotes[subIdx]));
    } else {
      // show top-level item's data
      setItems(normalizeFromInitial(target));
    }
  };

  const menuItems = [
    { label: "Balance Sheet", value: "BS" },
    { label: "P&L", value: "PL" },
    { label: "General Notes", value: "generalNotes" },
    { label: "Balance Sheet Notes", value: "BSNotes" },
    { label: "P&L Statement Notes", value: "PLNotes" },
    { label: "Cashflow", value: "cashflow" },
    { label: "SOCIE", value: "socie" },
    { label: "Disclosures", value: "disclosure" },
  ];

  return (
    <>
      <div>
        <div className={`d-flex ${commoncss.pageContent}`}>
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className={commoncss.w100}
            style={{ maxHeight: "100vh", overflowY: "hidden" }}
          >      
            <div className="fscp-header">
              <Breadcrumb
                style="margin-right:auto"
                addCls={"bgClr1"}
                items={[
                  {
                    label: "Template Library",
                    link: `/myfinV2/library/viewedit-library/${tempId}`,
                    className: `${commoncss.nav_cnt_color} clickable`,
                  },
                  { label: `${libraryName}`, link: "", className: "headerLable" },
                ]}
                headerActions={<></>}
              />

            </div>
     
            <div className={libcss.financialWrapper}>
                <div className={libcss.previewHeader}>
                <h2 className={libcss.previewTitle}>
                  Preview – {templateData?.templateName || "Template Name"}
                </h2>
              <Button
                btnColor={buttoncss.secondary}
                label={"Exit Preview"}
                onClick={() => {
                  history.push(`/myfinV2/library/viewedit-library/${tempId}`);
                }}
              />
            </div>
            <div
              className={`${libcss.libBody} ${commoncss.dFlex}  ${commoncss.hideScroll} m-2`}
            >
              <div className={`${libcss.previewS1} ${commoncss.hideScroll} w-20`}>
                <PreviewSidebar
                  menuItems={menuItems}
                  activeItem={0}
                  title={"Financial Statement Name"}
                  showData={showData}
                  templateData={templateData}
                  setSelCat={setSelCat}
                />
              </div>

              <div className={`${libcss.previewS2}  ${commoncss.hideScroll}`}>
                <div className={libcss.levelCatDiv}>
                  <div style={{ display: "grid", gap: 14 }}>
                    {(selCat !== "BS" || selCat !== "PL") &&
                      items.map((it) => {
                        if (it.type === "text") {
                          const html = it.text?.[0]?.content ?? "";
                          return (
                            <div
                              key={it.id}
                              style={{
                                background: "#fff",
                                border: "1px solid #e3efe7",
                                borderRadius: 14,
                                padding: 12,
                                boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                              }}
                            >
                              <div style={{ marginTop: 33 }}>
                                <QuillReactEditor value={html} disabled={true} />
                              </div>
                            </div>
                          );
                        }

                        if (it.type === "table") {
                          return (
                            <div
                              key={it.id}
                              style={{
                                background: "#fff",
                                border: "1px solid #e3efe7",
                                borderRadius: 14,
                                padding: 12,
                                boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                              }}
                            >
                              <div style={{ marginTop: 10 }}>
                                <PreviewTemplateTableGenerator
                                  forceOpen={it.id === 100}
                                  value={{
                                    tableTitle: it.tableTitle,
                                    tableRow: it.tableRow,
                                    tableColumn: it.tableColumn,
                                  }}
                                />
                              </div>
                            </div>
                          );
                        }

                        return null;
                      })}

                    {selCat === "BS" && (
                      <RenderBSPLTables
                        data={templateData?.grouping}
                        category={"BS"}
                        notesCount={templateData?.generalNotes?.length}
                      />
                    )}

                    {selCat === "PL" && (
                      <RenderBSPLTables
                        data={templateData?.grouping}
                        category={"PL"}
                        notesCount={templateData?.generalNotes?.length}
                      />
                    )}
                  </div>
                </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default PreviewSTTemplate;
